import React, { useState } from 'react';
import {  Button } from '@shadcn/components/ui/button';

import ProductDetailStep from './ProductDetailStep';
import { Dialog, DialogContent, DialogTrigger } from '@shadcn/components/ui/dialog';
import WizardNav from '@pages/JIFI/orders/Wizard/WizardNav';
import BulkInputSKUsStep from './BulkInputSKUsStep';
import CreateParcelStep from './CreateParcelStep';
import superagent from 'superagent';
import useProductStore from '@zustand/useProductStore';
import useParcelStore from '@zustand/useParcelStore';
import useVariantStore from '@zustand/useVariantStore';
import { backendUrl } from '@helpers/consts';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import SuccessStep from './SuccessStep';
import { toast } from 'react-toastify';

const STEPS = {
  PRODUCT_DETAILS : 0,
  CREATE_PARCEL: 1,
  BULK_INPUT_SKUS: 2,
  CONFIRMATION: 3,
  SUCCESS: 4
};

const LABELS = {
  BULK_INPUT_SKUS: 'Variants',
  CREATE_PARCEL: 'Parcel',
  PRODUCT_DETAILS: 'Product',
  CONFIRMATION: 'Review',
  SUCCESS: 'Success'
}

const ProductWizard = ( {refresh}) => {
  const [productName, selectedCategory, selectedFactory, selectedReportCategory, subCategory, selectedSubcategory, productNameExists] = useProductStore(state => [state.productName, state.selectedCategory, state.selectedFactory, state.selectedReportCategory, state.subCategory, state.selectedSubcategory, state.productNameExists])
  const {productID} = useParams();
  const [parcels] = useParcelStore(state => [state.parcels])
  
  const [currentStep, setCurrentStep] = useState(STEPS.PRODUCT_DETAILS);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {  resetState: resetVariantStore, variants, hasExistingSKUs, skusValidated } = useVariantStore();
  const {  reset: resetParcelStore } = useParcelStore();
  const { reset: resetProductStore } = useProductStore();
  
  const nextStep = () => {
    if (currentStep < Object.keys(STEPS).length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }

  const handleClose = () => {
    setIsOpen(false);
    setCurrentStep(STEPS.PRODUCT_DETAILS);
  };

  const onSave = () => {
    if(!productID) {  
      setIsLoading(true)
      superagent.post(`${backendUrl}/product/createProductWizard`).send({
        product: {
          name: productName,
          factory: selectedFactory.id,
          productCategory: selectedCategory.id,
          category: selectedCategory.name,
          newProductCategory: selectedReportCategory?.id,
          subCategory: selectedSubcategory?.value || subCategory,
        },
        parcels,
        variants: variants
      })
      .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
      .set('accept', 'json')
      .then(() => {
        setIsLoading(false);
        setCurrentStep(STEPS.SUCCESS);

        resetVariantStore();
        resetParcelStore();
        resetProductStore();
      })
      .catch(err => {
        console.error("Error creating product:", err?.response?.body?.response?.message)
        toast.error(err?.response?.body?.response?.message)
        setIsLoading(false)
      })
    } 
  }

  const renderConfirmationStep = () => {
    return (
      <div className="max-w-full lg:max-w-[58vw] mx-auto bg-white rounded-lg shadow-lg p-4 md:p-6 lg:p-8">
        <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-6 md:mb-8 border-b pb-4">Product Details Review</h3>
        
                  <div className="space-y-6 md:space-y-8">
          <div className="bg-gray-50 p-4 md:p-6 rounded-lg">
            <h4 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Product Name</span>
                <span className="font-medium">{productName}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Category</span>
                <span className="font-medium">{selectedCategory?.name}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Factory</span>
                <span className="font-medium">{selectedFactory?.name}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Report Category</span>
                <span className="font-medium">{selectedReportCategory?.name || 'Not selected'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Sub Category</span>
                <span className="font-medium">{selectedSubcategory?.label || subCategory || 'Not specified'}</span>
              </div>
            </div>
          </div>


          <div className="bg-gray-50 p-4 md:p-6 rounded-lg">
            <h4 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Variants ({variants?.length || 0})</h4>
            <div className="space-y-3 md:space-y-4">
              {variants?.map((variant, index) => (
                <div key={index} className="p-3 md:p-4 bg-white rounded border">
                  <div className="space-y-2">
                    <h5 className="font-medium text-gray-700 text-lg">{variant.name || 'N/A'}</h5>
                    <p className="text-sm text-gray-500">SKU: <span className="font-mono">{variant.sku || 'N/A'}</span></p>
                    <p className="text-sm text-gray-500">Color: <span className="font-medium">{variant.color || 'Not specified'}</span></p>
                    {variant.image && (
                      <div className="mt-2">
                        <img 
                          src={variant.image} 
                          alt={variant.name} 
                          className="w-16 h-16 object-cover rounded border"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 p-4 md:p-6 rounded-lg">
            <h4 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Parcels ({parcels?.length || 0})</h4>
            <div className="grid gap-3 md:gap-4">
              {parcels?.map((parcel, index) => (
                <div key={index} className="p-3 md:p-4 bg-white rounded border">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <p className="font-medium text-gray-700 text-base md:text-lg">{parcel.parcel_description || 'N/A'}</p>
                      <p className="text-sm text-gray-500">Declared Price: <span className="font-semibold text-green-600">${parcel.declared_price || '0'}</span></p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">Weight: <span className="font-medium">{parcel.weight || '0'}kg</span></p>
                      <p className="text-sm text-gray-500">Dimensions: <span className="font-mono text-xs">{parcel.length || '0'} × {parcel.width || '0'} × {parcel.depth || '0'} cm</span></p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 md:mt-8 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <svg className="w-5 h-5 mr-3 mt-0.5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-800 mb-1">Ready to create product</p>
              <p className="text-xs text-blue-600">
                Please review all information carefully. Once created, you can modify variants and parcels later.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-full">
          <Loader2 className="animate-spin h-10 w-10" />
        </div>
      );
    }

    

    switch (currentStep) {
      case STEPS.BULK_INPUT_SKUS:
        return (
          <>
            <BulkInputSKUsStep />
            
            <div className={`flex justify-between mt-4 ${STEPS.BULK_INPUT_SKUS === currentStep ? 'justify-end' : ''}`}>
            <Button onClick={previousStep}>Previous</Button>
              {!productID && (
                <Button
                  disabled={variants.length === 0 || !skusValidated || hasExistingSKUs}
                  onClick={nextStep}
                  title={
                    variants.length === 0 ? "Add variants first" :
                    !skusValidated ? "Please validate SKUs first" :
                    hasExistingSKUs ? "Fix existing SKUs first" : ""
                  }
                >
                  Next
                </Button>
              )}
            </div>
          </>
        );
      case STEPS.CREATE_PARCEL:
        return (
          <>
            <CreateParcelStep />
            <div className="flex justify-between mt-4">
              <Button onClick={previousStep}>Previous</Button>
              {!productID && (
                <Button disabled={parcels.length === 0} onClick={nextStep}>Next</Button>
              )}
            </div>
          </>
        );
      case STEPS.PRODUCT_DETAILS:
        return (
          <>
            <ProductDetailStep />
            <div className="flex justify-end mt-4">
              
              <Button
                disabled={productName === '' || !selectedCategory || !selectedFactory || productNameExists}
                onClick={nextStep}
                title={
                  productName === '' ? "Enter product name" :
                  !selectedCategory ? "Select category" :
                  !selectedFactory ? "Select factory" :
                  productNameExists ? "Product name already exists" : ""
                }
              >
                Next
              </Button>
            </div>
          </>
        );
      case STEPS.CONFIRMATION:
        return (
          <>
            {renderConfirmationStep()}
            <div className="flex justify-between mt-4">
              <Button onClick={previousStep}>Previous</Button>
              <Button onClick={onSave} variant="success">Save Changes</Button>
            </div>
          </>
        );
      case STEPS.SUCCESS:
        return <SuccessStep onClose={handleClose} />;
      default:
        return null;
    }
  };

  
  const toggleModal = () => {
    setIsOpen(!isOpen);
  }
  
  
  return (
    <Dialog open={isOpen} onOpenChange={toggleModal}>
      <DialogTrigger>
        <Button>Product Wizard</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] w-[95vw] md:w-[80vw] lg:w-[60vw] max-w-[60vw] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Product Wizard</h2>
           
          </div>
          <WizardNav
            steps={Object.keys(STEPS)

              .map((step) => LABELS[step])}
            activeTabProgress={currentStep + 1}
          />
          {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default ProductWizard;
